import { Dialog, DialogPanel, DialogTitle, DialogBackdrop, Description } from '@headlessui/react'
import { AlertTriangle } from 'lucide-react'
import React from "react"

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message?: string
  children?: React.ReactNode
  confirmText?: string
  cancelText?: string
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
  isDangerous?: boolean
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl'
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  children,
  confirmText = "确认",
  cancelText = "取消",
  onConfirm,
  onCancel,
  isLoading = false,
  isDangerous = false,
  maxWidth = 'md',
}) => {
  const maxWidthClass = {
    'sm': 'max-w-sm',
    'md': 'max-w-md',
    'lg': 'max-w-lg',
    'xl': 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl'
  }[maxWidth]
  return (
    <Dialog 
      open={isOpen} 
      onClose={onCancel}
      className="relative z-50"
      transition
    >
      <DialogBackdrop 
        transition 
        className="fixed inset-0 bg-black/50 duration-300 ease-out data-[closed]:opacity-0" 
      />
      
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel 
          transition
          className={`w-full ${maxWidthClass} max-h-[90vh] transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 flex flex-col`}
        >
          <div className="flex items-center mb-4">
            {isDangerous && (
              <div className="flex-shrink-0 mr-3">
                <AlertTriangle className="w-6 h-6 text-red-600" aria-hidden="true" />
              </div>
            )}
            <DialogTitle className="text-lg font-medium text-gray-900">
              {title}
            </DialogTitle>
          </div>

          {(message || children) && (
            <div className="mb-6 flex-1 overflow-y-auto">
              {message ? (
                <Description className="text-sm text-gray-600">
                  {message}
                </Description>
              ) : (
                children
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 flex-shrink-0">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 focus:outline-none focus:ring-2 ${
                isDangerous
                  ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                  : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  处理中...
                </div>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default ConfirmDialog